export const formatIDR = (n: number) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Math.round(n || 0));

export const formatHours = (h: number) => `${(h || 0).toFixed(1)} jam`;

export const monthLabel = (ym: string) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("id-ID", {
    month: "long",
    year: "numeric",
  });
};

export const currentPeriod = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

/** Kunci payroll di HR store (YYYY-MM), selaras dengan bulan tanggal akhir periode backend. */
export function payrollPeriodKeyFromEndDate(endDate: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(endDate)) return null;
  const d = new Date(`${endDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export const isValidPeriod = (value: string) => /^\d{4}-(0[1-9]|1[0-2])$/.test(value);

export const normalizePeriod = (value: unknown): string | null => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}`;
  }

  const raw = String(value || "").trim();
  if (!raw) return null;

  const yymm = raw.match(/^(\d{4})[-/](\d{1,2})$/);
  if (yymm) {
    const period = `${yymm[1]}-${String(Number(yymm[2])).padStart(2, "0")}`;
    return isValidPeriod(period) ? period : null;
  }

  const mmyy = raw.match(/^(\d{1,2})[-/](\d{4})$/);
  if (mmyy) {
    const period = `${mmyy[2]}-${String(Number(mmyy[1])).padStart(2, "0")}`;
    return isValidPeriod(period) ? period : null;
  }

  const lowered = raw.toLowerCase();
  const monthMap: Record<string, number> = {
    januari: 1,
    january: 1,
    februari: 2,
    february: 2,
    maret: 3,
    march: 3,
    april: 4,
    mei: 5,
    may: 5,
    juni: 6,
    june: 6,
    juli: 7,
    july: 7,
    agustus: 8,
    august: 8,
    september: 9,
    oktober: 10,
    october: 10,
    november: 11,
    desember: 12,
    december: 12,
  };

  for (const [monthName, monthNo] of Object.entries(monthMap)) {
    if (!lowered.includes(monthName)) continue;
    const year = lowered.match(/(19|20)\d{2}/)?.[0];
    if (!year) continue;
    return `${year}-${String(monthNo).padStart(2, "0")}`;
  }

  return null;
};

/** YYYY-MM-DD (atau DD-MM-YYYY tidak berpading) → DD-MM-YYYY untuk tampilan. */
export function formatDateDmyFromYmd(raw: string | null | undefined, emptyToken: "-" | "—" = "-"): string {
  if (raw == null) return emptyToken;
  const s = String(raw).trim();
  if (!s) return emptyToken;

  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;

  const dmyPadded = s.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmyPadded) return `${dmyPadded[1]}-${dmyPadded[2]}-${dmyPadded[3]}`;

  const dmyLoose = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (dmyLoose) {
    return `${String(Number(dmyLoose[1])).padStart(2, "0")}-${String(Number(dmyLoose[2])).padStart(2, "0")}-${dmyLoose[3]}`;
  }

  const slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) {
    return `${String(Number(slash[1])).padStart(2, "0")}-${String(Number(slash[2])).padStart(2, "0")}-${slash[3]}`;
  }

  const t = new Date(s);
  if (!Number.isNaN(t.getTime())) {
    const dd = String(t.getDate()).padStart(2, "0");
    const mm = String(t.getMonth() + 1).padStart(2, "0");
    const yyyy = t.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  }

  return s;
}

/**
 * Tanggal mentah dari timesheet (YYYY-MM-DD, DD-MM-YYYY, D/M/YYYY, dll.) → YYYY-MM-DD
 * untuk dibandingkan dengan tanggal cuti lokal.
 */
export function parseToYmd(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;

  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  const dmyPadded = s.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmyPadded) {
    return `${dmyPadded[3]}-${dmyPadded[2]}-${dmyPadded[1]}`;
  }

  const dmyLoose = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (dmyLoose) {
    const dd = String(Number(dmyLoose[1])).padStart(2, "0");
    const mm = String(Number(dmyLoose[2])).padStart(2, "0");
    return `${dmyLoose[3]}-${mm}-${dd}`;
  }

  const slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) {
    const dd = String(Number(slash[1])).padStart(2, "0");
    const mm = String(Number(slash[2])).padStart(2, "0");
    return `${slash[3]}-${mm}-${dd}`;
  }

  const t = new Date(s);
  if (!Number.isNaN(t.getTime())) {
    const y = t.getFullYear();
    const m = String(t.getMonth() + 1).padStart(2, "0");
    const day = String(t.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  return null;
}

export function formatDateDmyFromDate(d: Date | null | undefined): string {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return "-";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const y = d.getFullYear();
  return `${day}-${month}-${y}`;
}

/**
 * Menampilkan waktu titik (bukan durasi kerja) dalam format 24 jam HH:mm:ss.
 * Menangani nilai umum dari Excel: "9:30 AM", "2:15:30 PM", "14:00", "2026-01-15 09:00:00".
 * Jika komponen jam ≥ 24 (mis. total durasi 40:30:00), dikembalikan tanpa diubah.
 */
export function formatClockLikeTo24h(raw: string | null | undefined): string {
  const s0 = String(raw ?? "").trim();
  if (!s0 || s0 === "-") return "-";

  const dateTime = s0.match(
    /^(\d{4}-\d{2}-\d{2})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/i,
  );
  if (dateTime) {
    const datePart = dateTime[1];
    const hh = Number(dateTime[2]);
    const mm = Number(dateTime[3]);
    const ss = Number(dateTime[4] ?? 0);
    if ([hh, mm, ss].every((n) => Number.isFinite(n) && n >= 0) && hh < 24 && mm < 60 && ss < 60) {
      const clock = `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
      return `${datePart} ${clock}`;
    }
  }

  const m12 = s0.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(a\.?m\.?|p\.?m\.?)\s*$/i);
  if (m12) {
    let h = Number(m12[1]);
    const mm = Number(m12[2]);
    const ss = Number(m12[3] ?? 0);
    const ap = m12[4].replace(/\./g, "").toUpperCase();
    if (![h, mm, ss].every((n) => Number.isFinite(n) && n >= 0) || mm >= 60 || ss >= 60 || h < 1 || h > 12) {
      return s0;
    }
    if (ap.startsWith("P") && h !== 12) h += 12;
    if (ap.startsWith("A") && h === 12) h = 0;
    return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  }

  if (/\b(am|pm)\b/i.test(s0)) {
    const d = new Date(s0);
    if (!Number.isNaN(d.getTime())) {
      return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:${String(d.getSeconds()).padStart(2, "0")}`;
    }
  }
  const t = s0.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (t) {
    const h = Number(t[1]);
    const mm = Number(t[2]);
    const ss = Number(t[3] ?? 0);
    if ([h, mm, ss].every((n) => Number.isFinite(n) && n >= 0) && h < 24 && mm < 60 && ss < 60) {
      return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
    }
  }

  return s0;
}

/**
 * Menafsirkan waktu titik (alur sama seperti formatClockLikeTo24h) → detik dari tengah malam [0, 86400).
 * Mengembalikan null jika tidak bisa diparse (mis. durasi ≥ 24 jam).
 */
export function parseClockLikeToSecondsFromMidnight(raw: string | null | undefined): number | null {
  const s0 = formatClockLikeTo24h(raw);
  if (!s0 || s0 === "-") return null;
  const clockPart = s0.includes(" ") ? s0.split(/\s+/).pop()! : s0;
  const m = clockPart.match(/^(\d{1,2}):(\d{2}):(\d{2})$/);
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  const ss = Number(m[3]);
  if (![hh, mm, ss].every((n) => Number.isFinite(n) && n >= 0) || hh >= 24 || mm >= 60 || ss >= 60) return null;
  return hh * 3600 + mm * 60 + ss;
}